/**
 * VideoPlayer Component
 * Enhanced video player with timecode support and analysis integration
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Paper, Stack, Text, Button, Group, Loader, Alert, Box } from '@mantine/core';
import { IconPlayerPlay, IconPlayerPause, IconAlertCircle } from '@tabler/icons-react';
import { timeToSecs } from '../../lib/utils';
import type { VideoPlayerProps } from '../../lib/types';

const formatTime = (t: number): string =>
  `${Math.floor(t / 60)}:${Math.floor(t % 60)
    .toString()
    .padStart(2, '0')}`;

export default function VideoPlayer({
  url,
  timecodeList,
  requestedTimecode,
  isLoadingVideo,
  videoError,
  jumpToTimecode,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [scrubberTime, setScrubberTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [currentCaption, setCurrentCaption] = useState<string | null>(null);
  
  const currentSecs = duration * scrubberTime || 0;
  const currentPercent = scrubberTime * 100;
  
  const timecodeListReversed = useMemo(
    () => timecodeList?.slice().reverse(),
    [timecodeList],
  );

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  }, [isPlaying]);

  const updateDuration = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const updateTime = () => {
    if (!videoRef.current || isScrubbing) return;
    
    setScrubberTime(videoRef.current.currentTime / videoRef.current.duration);

    if (timecodeList && timecodeListReversed) {
      const caption = timecodeListReversed.find(
        (t) => timeToSecs(t.time) <= videoRef.current!.currentTime,
      )?.text;
      setCurrentCaption(caption || null);
    }
  };

  const onPlay = () => setIsPlaying(true);
  const onPause = () => setIsPlaying(false);

  useEffect(() => {
    setScrubberTime(0);
    setIsPlaying(false);
  }, [url]);

  useEffect(() => {
    if (videoRef.current && requestedTimecode !== null && requestedTimecode !== undefined) {
      videoRef.current.currentTime = requestedTimecode;
    }
  }, [requestedTimecode]);

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.key !== ' '
      ) {
        return;
      }
      e.preventDefault();
      togglePlay();
    };

    window.addEventListener('keypress', onKeyPress);
    return () => window.removeEventListener('keypress', onKeyPress);
  }, [togglePlay]);

  const handleTimecodeClick = (time: number) => {
    if (jumpToTimecode) {
      jumpToTimecode(time);
    }
  };

  if (isLoadingVideo) {
    return (
      <Paper shadow="sm" p="xl" style={{ height: 400 }}>
        <Stack align="center" justify="center" h="100%">
          <Loader size="lg" />
          <Text color="dimmed">Processing video...</Text>
        </Stack>
      </Paper>
    );
  }

  if (videoError) {
    return (
      <Paper shadow="sm" p="xl" style={{ height: 400 }}>
        <Alert 
          icon={<IconAlertCircle size={16} />} 
          color="red" 
          title="Video Error"
        >
          {videoError}
        </Alert>
      </Paper>
    );
  }

  if (!url) {
    return (
      <Paper shadow="sm" p="xl" style={{ height: 400 }}>
        <Stack align="center" justify="center" h="100%">
          <Text color="dimmed" size="lg">
            No video loaded
          </Text>
          <Text color="dimmed" size="sm" style={{ textAlign: 'center' }}>
            Select a video URL to start analyzing content
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" withBorder>
      <Box pos="relative">
        <video
          ref={videoRef}
          src={url}
          onClick={togglePlay}
          preload="metadata"
          crossOrigin="anonymous"
          onDurationChange={updateDuration}
          onTimeUpdate={updateTime}
          onPlay={onPlay}
          onPause={onPause}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '400px',
            backgroundColor: '#000'
          }}
        />

        {/* Current Caption Overlay */}
        {currentCaption && (
          <Box
            pos="absolute"
            bottom={60}
            left={0}
            right={0}
            p="sm"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Text size="sm">{currentCaption}</Text>
          </Box>
        )}
      </Box>

      {/* Video Controls */}
      <Box p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
        {/* Progress Bar with Timecode Markers */}
        <Box pos="relative" mb="sm">
          <input
            type="range"
            min="0"
            max="1"
            value={scrubberTime || 0}
            step="0.000001"
            onChange={(e) => {
              const newTime = parseFloat(e.target.value);
              setScrubberTime(newTime);
              if (videoRef.current) {
                videoRef.current.currentTime = newTime * duration;
              }
            }}
            onPointerDown={() => setIsScrubbing(true)}
            onPointerUp={() => setIsScrubbing(false)}
            style={{
              width: '100%',
              height: '6px',
              background: `linear-gradient(to right, var(--mantine-color-blue-6) 0%, var(--mantine-color-blue-6) ${currentPercent}%, var(--mantine-color-gray-3) ${currentPercent}%, var(--mantine-color-gray-3) 100%)`,
              borderRadius: '3px',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          
          {/* Timecode Markers */}
          {timecodeList?.map((timecode, i) => {
            const secs = timeToSecs(timecode.time);
            const pct = duration > 0 ? (secs / duration) * 100 : 0;

            return (
              <Box
                key={i}
                pos="absolute"
                top={-2}
                left={`${pct}%`}
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: 'var(--mantine-color-red-6)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  transform: 'translateX(-50%)',
                  zIndex: 10
                }}
                onClick={() => handleTimecodeClick(secs)}
                title={`${timecode.time}: ${timecode.text}`}
              />
            );
          })}
        </Box>

        {/* Control Bar */}
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Button
              variant="subtle"
              size="sm"
              onClick={togglePlay}
              leftSection={isPlaying ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <Text size="sm" color="dimmed">
              {formatTime(currentSecs)} / {formatTime(duration)}
            </Text>
          </Group>
          
          {timecodeList && timecodeList.length > 0 && (
            <Text size="xs" color="dimmed">
              {timecodeList.length} markers
            </Text>
          )}
        </Group>
      </Box>
    </Paper>
  );
}
